import { useState, useEffect, useCallback, useRef } from "react";

type State<T> = { data: T | null; loading: boolean; error: string | null };

export function useApi<T>(fetcher: (() => Promise<T>) | null, deps: any[] = []) {
  const [state, setState] = useState<State<T>>({ data: null, loading: !!fetcher, error: null });
  const isMounted = useRef(true);

  const run = useCallback(async () => {
    if (!fetcher) return;
    setState(s => ({ ...s, loading: true, error: null }));
    try {
      const data = await fetcher();
      if (isMounted.current) setState({ data, loading: false, error: null });
    } catch (e: any) {
      if (isMounted.current) setState({ data: null, loading: false, error: e.message });
    }
  }, deps);

  useEffect(() => { run(); }, [run]);
  useEffect(() => () => { isMounted.current = false; }, []);

  return { ...state, refetch: run };
}

export function useMutation<TArgs extends any[], TResult>(
  fn: (...args: TArgs) => Promise<TResult>
) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mutate = useCallback(async (...args: TArgs): Promise<TResult | null> => {
    setLoading(true); setError(null);
    try {
      const result = await fn(...args);
      setLoading(false);
      return result;
    } catch (e: any) {
      setError(e.message);
      setLoading(false);
      return null;
    }
  }, [fn]);

  return { mutate, loading, error };
}
